/**
 * Shell — the four tabs and the screen above them.
 *
 * Task 04 built the frame; task 05 fills «Треки» (`screens/Tracks.tsx`), task 10 «Поиск»
 * (`screens/Search.tsx`), task 11 «Моё» (`screens/My.tsx`), task 13 «Видео»
 * (`screens/Video.tsx`). A tab that has no screen yet draws its title and one honest sentence
 * about what will be there — none is left since task 13, and the rule stays for the next one.
 *
 * **The empty state says what will be here, not «пусто».** A person who opens a new product
 * and sees «Ничего нет» concludes it is broken; a person who reads «Здесь появятся клипы
 * канала» concludes it is early. They are looking at the same screen.
 *
 * ## The tab order is the specification's
 *
 * «Треки», «Видео», «Поиск», «Моё» — `spec.md` «Решение». It is also the order of use, and
 * the last tab is the one a thumb reaches most easily on a large phone.
 *
 * ## The mini player lives here, not in a screen
 *
 * Task 07, checklist item «Мини-плеер внизу на вкладках „Треки“, „Видео“, „Поиск“, „Моё“».
 * It is part of the frame, between the scroller and the tabs, so switching a tab can neither
 * unmount it nor cover it — and the sound, which lives outside React altogether
 * (`engine/element.ts`), has nothing to do with which screen is drawn.
 *
 * ## The saved queue is read here (task 08)
 *
 * The shell is on screen exactly while the listener is signed in, so it is what starts the
 * sync (`engine/sync.ts`) and stops it. On mount the saved queue is read; if it names a track,
 * the player holds it at its second in the mini player, and the first «Играть» carries on from
 * there. Nothing plays: a launch is not a tap. Until task 41 a «Продолжить?» line stood above the
 * mini player as a shortcut to that same tap; the owner removed it on 2026-10-04 («это не нужно»).
 *
 * ## A link opens its track here (task 15)
 *
 * A launch from `startapp=t42` opens that track instead of the saved queue: `app/deeplink.ts`
 * makes it the queue and holds it, and «Сейчас играет» opens over it — which is why the screen's
 * open state lives here and is handed to the mini player that draws it. Only when the link names
 * no track, or the track cannot be had, is the saved queue read and held, as on any launch. The
 * opening is kept in a ref, so `StrictMode`'s second mount waits on the same request instead of
 * starting another that would find the queue already taken.
 *
 * ## A link opens its clip in «Видео» (task 16)
 *
 * `startapp=v19` — a line of the weekly digest — puts that clip in the frame of «Видео»
 * (`app/deeplink.ts`) and opens the tab once it is there. The music is not touched, so the saved
 * queue is read as on any launch. A listener who has already moved to another tab by the time the
 * clip arrives stays where they went: their tap is newer than the link.
 *
 * ## A line of trouble is felt (task 50)
 *
 * The moment the player says what went wrong («Звук не идёт», «Трек не загрузился», …), the phone
 * buzzes once with Telegram's error vibration — here, once, and not in each of the two places that
 * draw the line (`components/PlayerControls.tsx`).
 *
 * ## So are the places in long tracks (task 49)
 *
 * `GET /api/positions` on the same mount, so a mix tapped in a list can go on where the
 * listener stopped — on this phone or another — with no request between the tap and `play()`
 * (`engine/positions.ts`).
 *
 * ## The hearts are read here too (task 11)
 *
 * Beside the saved queue, on the same mount: `GET /api/favorites/ids`, so every heart on every
 * list is drawn right from the first screen (`store/favorites.ts`). A heart that could not be
 * saved says so in a line above the mini player (`components/FavoriteNotice.tsx`).
 *
 * ## «Поиск» and «Моё» open lists in «Треки» (tasks 10, 11)
 *
 * An artist or a riddim tapped in the results opens in its section of «Треки», so the tab
 * belongs to the shell and the search screen only names the list. The library slice is told
 * which list and on top of which section (`openInSection`), the scroller goes to the top as on
 * any tab switch, and «Треки» is drawn with that list open.
 *
 * ## One scroller, owned here
 *
 * The screen above the tabs is the only element of the app that scrolls, and this component
 * hands it to the lists through `ScrollPortContext` (`app/scroll-port.ts` says why a context
 * and not a DOM search). Switching tabs puts it back at the top: the next tab's content has
 * nothing to do with how far down the last one was read.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { parseStartParam } from '@fdm/shared';

import { FavoriteNotice } from '../components/FavoriteNotice';
import { MiniPlayer } from '../components/MiniPlayer';
import { MinimiseHint } from '../components/MinimiseHint';
import { player } from '../engine/player';
import { loadPlaces } from '../engine/positions';
import { telegram } from '../lib/telegram';
import { startSync, stopSync } from '../engine/sync';
import { video } from '../engine/video';
import type { VideoItem } from '../lib/api';
import { MyScreen } from '../screens/My';
import { SearchScreen } from '../screens/Search';
import { TracksScreen } from '../screens/Tracks';
import { VideoScreen } from '../screens/Video';
import { useFavorites } from '../store/favorites';
import type { Collection } from '../store/library';
import { useLibrary } from '../store/library';
import { usePlayer } from '../store/player';
import { useSession } from '../store/session';
import '../styles/player.css';

import { openStartTrack, openStartVideo } from './deeplink';
import { ScrollPortContext } from './scroll-port';

/** One tab. A link opens a track, not a tab (task 15); `id` is the tab's own name. */
export interface Tab {
  readonly id: string;
  readonly label: string;
  /** What this tab will hold, shown until the task that fills it lands. Absent once it has. */
  readonly promise?: string | undefined;
}

export const TABS: readonly Tab[] = Object.freeze([
  { id: 'tracks', label: 'Треки' },
  { id: 'video', label: 'Видео' },
  { id: 'search', label: 'Поиск' },
  { id: 'my', label: 'Моё' },
] as const);

/**
 * Task 66: a note, a screen, a magnifier and a heart over the four words — drawn in the lines of
 * the player's other icons. The word stays: it is the tab's name for a screen reader too.
 */
function TabIcon({ id }: { readonly id: string }): JSX.Element {
  let shape: JSX.Element;
  if (id === 'tracks') {
    shape = (
      <>
        <path d="M9 18V5l11-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="17" cy="16" r="3" />
      </>
    );
  } else if (id === 'video') {
    shape = (
      <>
        <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
        <path d="M10 9.5v5l4.5-2.5z" fill="currentColor" />
      </>
    );
  } else if (id === 'search') {
    shape = (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-4-4" />
      </>
    );
  } else {
    shape = (
      <path d="M12 20.3l-1.3-1.2C6 14.9 3 12.2 3 8.8 3 6.1 5.1 4 7.8 4c1.5 0 3 .7 4.2 1.9C13.2 4.7 14.7 4 16.2 4 18.9 4 21 6.1 21 8.8c0 3.4-3 6.1-7.7 10.3L12 20.3z" />
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="tabIcon"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {shape}
    </svg>
  );
}

export function Shell(): JSX.Element {
  const [current, setCurrent] = useState(TABS[0]?.id ?? 'tracks');
  const [port, setPort] = useState<HTMLElement | null>(null);
  const [nowOpen, setNowOpen] = useState(false);
  const opening = useRef<Promise<boolean> | undefined>(undefined);
  /** The listener has switched a tab since the launch: a clip from a link then stays unopened. */
  const moved = useRef(false);
  const tab = TABS.find((t) => t.id === current) ?? TABS[0];

  /** Every tab switch starts the next tab at the top — see «One scroller, owned here». */
  const goTo = useCallback(
    (id: string): void => {
      if (port !== null) port.scrollTop = 0;
      setCurrent(id);
    },
    [port],
  );

  /** A tap on a tab, a list opened from search or «Моё»: the listener's own move. */
  const moveTo = useCallback(
    (id: string): void => {
      moved.current = true;
      goTo(id);
    },
    [goTo],
  );

  const openInTracks = useCallback(
    (collection: Collection): void => {
      useLibrary.getState().openInSection(collection);
      moveTo('tracks');
    },
    [moveTo],
  );

  /** Task 68: «Смотреть все треки» under an empty «Новое». */
  const openAllTracks = useCallback((): void => {
    useLibrary.getState().showSection('tracks');
    moveTo('tracks');
  }, [moveTo]);

  /** Task 13: a clip found by search plays in «Видео», in the tab's one frame. */
  const openInVideo = useCallback(
    (item: VideoItem): void => {
      video().play(item);
      moveTo('video');
    },
    [moveTo],
  );

  useEffect(() => {
    const sync = startSync();
    void useFavorites.getState().load();
    // Task 49: where the listener stopped in each long track, before the first tap needs it.
    void loadPlaces();
    // Task 50: see «A line of trouble is felt».
    const stopFeeling = usePlayer.subscribe((now, before) => {
      if (now.problem !== undefined && now.problem !== before.problem) telegram.haptic('error');
    });
    let mounted = true;
    const restore = (): void => {
      void sync.restore().then((positionS) => {
        if (mounted && positionS !== undefined) player().cue(positionS);
      });
    };

    const target = parseStartParam(useSession.getState().session?.startParam);
    if (target.kind === 'track') {
      opening.current ??= openStartTrack(target.trackId);
      void opening.current.then((opened) => {
        if (!mounted) return;
        if (opened) setNowOpen(true);
        else restore();
      });
    } else if (target.kind === 'video') {
      restore();
      // In the same ref as a track's, for the same reason: `StrictMode`'s second mount must wait
      // on this request, not start one that finds the clip already chosen by the first.
      opening.current ??= openStartVideo(target.videoId);
      void opening.current.then((opened) => {
        if (mounted && opened && !moved.current) setCurrent('video');
      });
    } else {
      restore();
    }
    return () => {
      mounted = false;
      stopFeeling();
      stopSync(sync);
    };
  }, []);

  return (
    <div className="shell">
      <ScrollPortContext.Provider value={port}>
        <main ref={setPort} className="screen">
          <div className="screenBody">
            {tab?.id === 'tracks' ? (
              <TracksScreen />
            ) : tab?.id === 'video' ? (
              <VideoScreen />
            ) : tab?.id === 'search' ? (
              <SearchScreen onOpenCollection={openInTracks} onOpenVideo={openInVideo} />
            ) : tab?.id === 'my' ? (
              <MyScreen onOpenCollection={openInTracks} onOpenAllTracks={openAllTracks} />
            ) : (
              <>
                <h1 className="screenTitle">{tab?.label}</h1>
                <p className="empty">{tab?.promise}</p>
              </>
            )}
          </div>
        </main>
      </ScrollPortContext.Provider>

      {/* Task 07: between the screen and the tabs, so it is on every tab and covers none. */}
      <FavoriteNotice />
      <MinimiseHint />
      <MiniPlayer nowOpen={nowOpen} onNowOpenChange={setNowOpen} onOpenCollection={openInTracks} />

      <nav className="tabs" aria-label="Разделы">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={item.id === current ? 'tab tabCurrent' : 'tab'}
            aria-current={item.id === current ? 'page' : undefined}
            onClick={() => {
              moveTo(item.id);
            }}
          >
            <TabIcon id={item.id} />
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
